/**
 * PlayerControls - Media controls shared by the Blitz player and the chapter
 * audio overlay. Play/pause is always rendered; prev/next and up/down appear
 * only when their handlers are supplied.
 *
 * `variant` picks the palette: 'dark' for the always-dark Blitz page, 'card'
 * for the theme-aware chapter overlay.
 */

const VARIANTS = {
  dark: {
    secondary: 'bg-gray-700 hover:bg-gray-600 text-white',
    accent: 'bg-purple-600 hover:bg-purple-700 text-white',
  },
  card: {
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white',
    accent: 'bg-primary hover:bg-primary/90 text-white',
  },
};

function PlayerControls({
  isPlaying,
  isLoading = false,
  disabled = false,
  onPlay,
  onPause,
  onPrev,
  onNext,
  hasPrev = true,
  hasNext = true,
  onUp,
  onDown,
  canUp = false,
  canDown = false,
  upTitle = 'Up',
  downTitle = 'Down',
  variant = 'dark',
}) {
  const palette = VARIANTS[variant] || VARIANTS.dark;
  const secondary = `rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${palette.secondary}`;

  return (
    <div className="flex items-center justify-center gap-3">
      {/* Up Arrow - Level navigation */}
      {onUp && (
        <button
          onClick={onUp}
          disabled={!canUp || isLoading}
          className={`p-2 ${secondary}`}
          title={upTitle}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}

      {/* Previous Button */}
      {onPrev && (
        <button
          onClick={onPrev}
          disabled={!hasPrev || isLoading}
          className={`p-3 ${secondary}`}
          title="Previous"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.445 14.832A1 1 0 0010 14v-2.798l5.445 3.63A1 1 0 0017 14V6a1 1 0 00-1.555-.832L10 8.798V6a1 1 0 00-1.555-.832l-6 4a1 1 0 000 1.664l6 4z" />
          </svg>
        </button>
      )}

      {/* Play/Pause Button */}
      <button
        onClick={isPlaying ? onPause : onPlay}
        disabled={isLoading || disabled}
        className={`p-4 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${palette.accent}`}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isLoading ? (
          <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : isPlaying ? (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        )}
      </button>

      {/* Next Button */}
      {onNext && (
        <button
          onClick={onNext}
          disabled={!hasNext || isLoading}
          className={`p-3 ${secondary}`}
          title="Next"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4.555 5.168A1 1 0 003 6v8a1 1 0 001.555.832L10 11.202V14a1 1 0 001.555.832l6-4a1 1 0 000-1.664l-6-4A1 1 0 0010 6v2.798L4.555 5.168z" />
          </svg>
        </button>
      )}

      {/* Down Arrow - Level navigation */}
      {onDown && (
        <button
          onClick={onDown}
          disabled={!canDown || isLoading}
          className={`p-2 ${secondary}`}
          title={downTitle}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default PlayerControls;
